import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { Layers, Plus, X } from 'lucide-react';
import { useAcademyStore } from '../../../store/academyStore';
import { ModuleSuccess } from '../../ui/ModuleSuccess';
import { LessonWrapper, LessonPage } from '../../ui/LessonWrapper';

// ============ LESSON CONTENT ============
const LESSONS: LessonPage[] = [
    {
        id: 1,
        title: "Legos of Code",
        icon: "🧱",
        content: (
            <div className="space-y-6">
                <div className="bg-indigo-50 p-6 rounded-2xl border-2 border-indigo-200">
                    <h3 className="font-bold text-xl text-indigo-800 mb-3">Don't Repeat Yourself (DRY)</h3>
                    <p className="text-slate-700 mb-4">
                        Imagine building a LEGO castle. You don't mold every single brick from plastic. You use pre-made bricks!
                    </p>
                    <p className="text-slate-700">
                        <strong>React Components</strong> are those bricks. You build a "Button" once, and use it 1,000 times.
                    </p>
                </div>
            </div>
        )
    },
    {
        id: 2,
        title: "The Syntax",
        icon: "📜",
        content: (
            <div className="space-y-6">
                <div className="bg-slate-900 text-blue-300 p-4 rounded-xl font-mono text-sm">
                    <div><span className="text-purple-400">function</span> <span className="text-yellow-400">MyButton</span>() {'{'}</div>
                    <div className="pl-4"><span className="text-purple-400">return</span> (</div>
                    <div className="pl-8 text-white">{'<button>Click Me</button>'}</div>
                    <div className="pl-4">);</div>
                    <div>{'}'}</div>
                </div>
                <div className="bg-white p-4 rounded-xl border border-slate-200">
                    <p className="text-slate-600 text-sm">
                        It looks like HTML, but it's JavaScript! This syntax is called <strong>JSX</strong>.
                        We capitalize component names (e.g., <code>MyButton</code>) to tell them apart from normal HTML tags (e.g., <code>button</code>).
                    </p>
                </div>
            </div>
        )
    },
    {
        id: 3,
        title: "Mission Brief",
        icon: "🚀",
        content: (
            <div className="space-y-6">
                <div className="bg-blue-50 p-6 rounded-2xl border-2 border-blue-200">
                    <h3 className="font-bold text-xl text-blue-800 mb-3">Compose the App</h3>
                    <p className="text-slate-700 mb-4">
                        We have a blank canvas.
                        Use the <code>&lt;Card /&gt;</code> component to build a list.
                    </p>
                    <p className="font-bold text-blue-900">
                        Add at least 3 Cards to deploy the app!
                    </p>
                </div>
            </div>
        )
    }
];

// ============ GAME COMPONENT ============
const ComponentsGame = ({ onComplete }: { onComplete: () => void }) => {
    const [components, setComponents] = useState<number[]>([]);

    const addComponent = () => {
        if (components.length < 6) {
            setComponents([...components, Date.now()]);
        }
    };

    const removeComponent = (id: number) => {
        setComponents(components.filter(c => c !== id));
    };

    const checkWin = () => {
        if (components.length >= 3) {
            onComplete();
        }
    };

    return (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
            {/* Visualizer */}
            <div className="bg-slate-800 p-8 rounded-3xl border border-slate-700 min-h-[400px] relative">
                <div className="absolute top-4 left-4 text-xs font-bold text-slate-500 uppercase tracking-widest">
                    App Canvas
                </div>

                <div className="mt-8 space-y-4">
                    {/* Header Component (Always there) */}
                    <div className="p-4 rounded-xl border-2 border-indigo-500/50 bg-indigo-900/20 text-center text-indigo-300 font-bold border-dashed">
                        &lt;Header /&gt;
                    </div>

                    {/* Dynamic Components */}
                    <div className="grid grid-cols-2 gap-4">
                        <AnimatePresence>
                            {components.map(id => (
                                <motion.div
                                    key={id}
                                    layout
                                    initial={{ scale: 0 }}
                                    animate={{ scale: 1 }}
                                    exit={{ scale: 0 }}
                                    className="p-4 rounded-xl border-2 border-blue-500 bg-blue-900/20 text-blue-300 font-bold flex items-center justify-between"
                                >
                                    <span>&lt;Card /&gt;</span>
                                    <button onClick={() => removeComponent(id)} className="text-red-400 hover:bg-red-900/50 p-1 rounded">
                                        <X size={16} />
                                    </button>
                                </motion.div>
                            ))}
                        </AnimatePresence>
                    </div>

                    {/* Footer (Always there) */}
                    {components.length > 0 && (
                        <div className="p-4 rounded-xl border-2 border-indigo-500/50 bg-indigo-900/20 text-center text-indigo-300 font-bold border-dashed mt-auto">
                            &lt;Footer /&gt;
                        </div>
                    )}
                </div>
            </div>

            {/* Explainer / Controls */}
            <div className="space-y-6">
                <div className="bg-indigo-900/20 p-6 rounded-2xl border border-indigo-500/30">
                    <h3 className="font-bold text-indigo-300 mb-2 flex items-center gap-2">
                        <Layers size={20} /> Concept: Reusability
                    </h3>
                    <p className="text-sm text-indigo-200">
                        Instead of writing HTML 50 times, you define a component <strong>ONCE</strong> and use it everywhere.
                    </p>
                </div>

                <div className="bg-slate-800 p-6 rounded-2xl border border-slate-700">
                    <div className="font-mono text-sm text-slate-400 mb-4 bg-slate-950 p-4 rounded-lg">
                        {`function App() {
  return (
    <div>
      <Header />
      ${components.map(() => '<Card />').join('\n      ')}
      <Footer />
    </div>
  );
}`}
                    </div>

                    <button
                        onClick={addComponent}
                        disabled={components.length >= 6}
                        className="w-full py-3 rounded-xl bg-blue-600 hover:bg-blue-500 text-white font-bold flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                        <Plus size={20} /> Add Component
                    </button>

                    {components.length >= 3 && (
                        <motion.button
                            initial={{ scale: 0.9, opacity: 0 }}
                            animate={{ scale: 1, opacity: 1 }}
                            onClick={checkWin}
                            className="w-full mt-4 py-3 rounded-xl bg-green-500 hover:bg-green-400 text-white font-bold animate-pulse"
                        >
                            Deploy App 🚀
                        </motion.button>
                    )}
                </div>
            </div>
        </div>
    );
};
import { AnimatePresence } from 'framer-motion';

// ============ MAIN COMPONENT ============
export const ReactComponents = () => {
    const { completeModule } = useAcademyStore();
    const [completed, setCompleted] = useState(false);

    const handleComplete = () => {
        setCompleted(true);
        completeModule('react-components', 100);
    };

    return (
        <LessonWrapper
            moduleName="React Components"
            moduleEmoji="🧱"
            moduleColor="indigo"
            lessons={LESSONS}
            completed={completed}
            activityLabel="App Builder"
        >
            <ComponentsGame onComplete={handleComplete} />
        </LessonWrapper>
    );
};

export default ReactComponents;
